import type {
  AddonGroup,
  AddonRowValue,
} from "../../qrOrder/components/SetMenuAddonPicker";
import { unitsOf } from "../../../utils/addonQuantity";

/**
 * The cashier's choice groups as the menu-item-details endpoint returns them:
 * keyed by `option_id`, not `_id`, which is the one shape difference between
 * this side and the qr side.
 */
export interface CashierGroup {
  option_id?: string;
  addon_name?: string;
  items?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface SetSelection {
  item: Record<string, unknown>;
  spice_level: unknown;
  quantity: number;
}

/**
 * The picker speaks `_id`; the cashier endpoint speaks `option_id`. One mapping
 * here rather than a second group shape inside the shared component.
 */
export const toPickerGroups = (groups: CashierGroup[] = []): AddonGroup[] =>
  (groups || []).map((group) => ({ ...group, _id: group?.option_id } as AddonGroup));

/**
 * Turn the picker's row list into the `(groups, selectionsByIndex)` pair
 * `buildSetMenuAddons` reads.
 *
 * The two describe the same thing differently: a row already carries its group,
 * its item, its level and its count, while `buildSetMenuAddons` wants a group
 * array indexed in step with a selection map. Rather than teach the builder a
 * second input shape — it is the one place the order payload is assembled, on
 * both the order screen and bulk settlement — the rows are projected onto the
 * shape it already takes.
 *
 * Empty rows are dropped: a group short of its components leaves one behind,
 * and it is not something ordered.
 *
 * The full catalogue item is resolved back out of `groups`, because the row
 * carries only what the qr payload needs — `consumption_tax_rate` and the rest
 * of the component's own fields live on the catalogue entry, and the builder
 * reads them.
 */
export const rowsToSetSelections = (
  rows: AddonRowValue[] = [],
  groups: CashierGroup[] = []
): { groups: CashierGroup[]; selectionsByIndex: Record<number, SetSelection> } => {
  const picked = (rows || []).filter((row) => row?.item_id);
  const selectionsByIndex: Record<number, SetSelection> = {};
  const orderedGroups: CashierGroup[] = [];

  picked.forEach((row, index) => {
    const group = (groups || []).find(
      (entry) => String(entry?.option_id) === String(row?.addon_id)
    );
    const catalogueItem = (group?.items || []).find(
      (entry) => String((entry as { _id?: string })?._id) === String(row?.item_id)
    );
    orderedGroups.push({
      option_id: row?.addon_id,
      addon_name: row?.addon_name as string,
      items: group?.items || [],
    });
    selectionsByIndex[index] = {
      // A row whose group is no longer configured cannot be resolved back to a
      // catalogue entry; the row's own copy of the price fields is what it was
      // ordered at, so it stands in.
      //
      // `_id` is restated from `item_id` and is not cosmetic: `buildSetMenuAddons`
      // opens with `if (!chosen?._id) return addons`, so a stand-in without it is
      // dropped from the payload entirely — the cashier would be shown a total
      // including a component the order never carries, which is worse than the
      // short bill this fallback exists to prevent.
      item: (catalogueItem as Record<string, unknown>) || {
        ...(row as Record<string, unknown>),
        _id: row?.item_id,
      },
      spice_level: row?.spice_level ?? null,
      quantity: unitsOf(row),
    };
  });

  return { groups: orderedGroups, selectionsByIndex };
};
